import { useMemo, useRef } from 'react';
import type { Options } from 'uplot';
import { ChartCanvas, readCssVar } from './charts/ChartCanvas';
import { ChartColorFallback } from '@/tokens/chart-color-fallbacks';

export type WaveformPanelProps = {
  /** Time-domain samples (single channel). */
  samples: number[];
  /** Sample rate in Hz, used to label the time window. */
  sampleRate: number;
  /** y-axis unit label (e.g. "g", "mm/s"). */
  unit?: string;
  state?: 'default' | 'loading';
  /** LinkedCursor position as a fraction (0..1) of the visible window. */
  cursorFraction?: number | null;
};

/**
 * Time-domain waveform chart, rendered with uPlot. Comes with:
 *   - drag-to-zoom on the x-axis (region select for replay-in-place)
 *   - double-click to reset zoom
 *   - crosshair + tooltip with millisecond + amplitude readout
 *   - high-perf rendering for 25k+ samples via Float64Array path
 *
 * uPlot handles point density natively — no manual min/max envelope
 * downsampling needed (it draws one segment per pixel column).
 */
export function WaveformPanel({
  samples,
  sampleRate,
  unit = 'g',
  state = 'default',
  cursorFraction = null,
}: WaveformPanelProps) {
  const cursorRef = useRef(cursorFraction);
  cursorRef.current = cursorFraction;
  const { data, peak, durationMs } = useMemo(() => {
    const n = samples.length;
    const xs = new Float64Array(n);
    let p = 0;
    for (let i = 0; i < n; i++) {
      xs[i] = (i / sampleRate) * 1000;
      const v = Math.abs(samples[i]);
      if (v > p) p = v;
    }
    const ys = Float64Array.from(samples);
    return {
      data: [xs, ys] as [Float64Array, Float64Array],
      peak: p || 1,
      durationMs: (n / sampleRate) * 1000,
    };
  }, [samples, sampleRate]);

  const options = useMemo<Omit<Options, 'width' | 'height'>>(() => {
    const stroke = readCssVar('c-info', ChartColorFallback.info);
    const axisColor = readCssVar('c-gray-700', ChartColorFallback.gray700);
    const gridColor = readCssVar('c-gray-300', ChartColorFallback.gray300);
    return {
      scales: {
        // uPlot gotcha: a static `range: [min, max]` overrides any
        // user-set scale on every commit, silently breaking drag-to-zoom
        // and dblclick reset. The initial view is set imperatively in
        // the `init` hook below so drag/dblclick own the scale after
        // construction. See SpectrumPanel for the same fix + writeup.
        x: { time: false },
        y: {},
      },
      axes: [
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 0.5 },
          values: (_u, vals) => vals.map((v) => `${v.toFixed(1)} ms`),
        },
        {
          stroke: axisColor,
          grid: { stroke: gridColor, width: 0.5 },
          size: 60,
          values: (_u, vals) =>
            vals.map((v) =>
              Math.abs(v) < 1 ? v.toFixed(3) : v.toPrecision(3),
            ),
        },
      ],
      cursor: {
        x: true,
        y: true,
        drag: { x: true, y: false, setScale: true },
      },
      series: [
        { label: 'ms' },
        {
          label: unit,
          stroke,
          width: 1,
          spanGaps: true,
          value: (_u, raw) =>
            raw == null
              ? '—'
              : Math.abs(raw) < 1
                ? raw.toFixed(3)
                : raw.toPrecision(3),
        },
      ],
      hooks: {
        init: [
          (u) => {
            u.setScale('x', { min: 0, max: durationMs });
            u.setScale('y', { min: -peak * 1.1, max: peak * 1.1 });
          },
        ],
        drawClear: [
          (u) => {
            const cf = cursorRef.current;
            if (cf == null) return;
            const f = Math.max(0, Math.min(1, cf));
            const xMin = u.scales.x.min!;
            const xMax = u.scales.x.max!;
            const x = Math.round(
              u.valToPos(xMin + (xMax - xMin) * f, 'x', true),
            );
            u.ctx.save();
            u.ctx.setLineDash([4, 2]);
            u.ctx.lineWidth = 1.4;
            u.ctx.strokeStyle = readCssVar('c-warn', ChartColorFallback.warn);
            u.ctx.beginPath();
            u.ctx.moveTo(x, u.bbox.top);
            u.ctx.lineTo(x, u.bbox.top + u.bbox.height);
            u.ctx.stroke();
            u.ctx.restore();
          },
        ],
      },
    };
  }, [durationMs, peak, unit]);

  if (state === 'loading' || samples.length === 0) {
    return (
      <div
        role="img"
        aria-label="Waveform loading"
        aria-busy={state === 'loading'}
        className="flex h-30 items-center justify-center rounded-r-card border border-gray-300 bg-gray-50 text-fs-meta text-gray-700"
        style={{ minHeight: '7.5rem' }}
      >
        {state === 'loading' ? 'Loading waveform…' : 'No waveform data'}
      </div>
    );
  }

  const a11yLabel = `Time-domain waveform, ${samples.length} samples at ${sampleRate} Hz, peak ${peak.toFixed(2)} ${unit}. Drag to zoom, double-click to reset.`;

  return (
    <figure className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-3">
      <ChartCanvas
        data={data}
        options={options}
        aspect={4.5}
        ariaLabel={a11yLabel}
      />
      <figcaption className="mt-sp-1 flex justify-between text-fs-eyebrow uppercase tracking-[0.14em] text-gray-700">
        <span>0 ms</span>
        <span>
          ±{peak.toFixed(2)} {unit} · drag to zoom
        </span>
        <span>{durationMs.toFixed(0)} ms</span>
      </figcaption>
      {cursorFraction != null ? (
        <div
          aria-hidden
          className="sr-only"
          data-cursor-fraction={cursorFraction}
        />
      ) : null}
    </figure>
  );
}
